import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

interface FailedJob {
  id: number;
  uuid: string;
  job: string;
  failed_at: string;
  exception: string;
}

interface QueueStats {
  pending: number;
  failed: number;
  worker_status: 'active' | 'idle' | 'stopped';
  oldest_pending_minutes: number | null;
}

// Inline SVG Icons
const Icons: Record<string, React.FC<{ size?: number }>> = {
  User: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Mail: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Shield: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Building: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" /><line x1="9" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="9" y2="16" /><line x1="15" y1="16" x2="15" y2="16" /><line x1="9" y1="8" x2="9" y2="8" /><line x1="15" y1="8" x2="15" y2="8" />
    </svg>
  ),
  Save: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Zap: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  RefreshCw: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  Trash2: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),
  AlertTriangle: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

const defaultSettings = {
  name: '',
  email: '',
  avatar: '',
  default_sender_id: '',
  rate_limit_per_hour: 100,
  bounce_handling: 'mark',
  unsubscribe_handling: 'mark',
  company_name: '',
  timezone: 'UTC',
  date_format: 'Y-m-d',
};

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  // Queue tab state
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [failedJobs, setFailedJobs] = useState<FailedJob[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<number | 'all' | 'clear' | null>(null);
  const queueIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchQueueData = useCallback(async (showLoader = false) => {
    if (showLoader) setQueueLoading(true);
    try {
      const [statusRes, failedRes] = await Promise.all([
        api.get('/queue/status'),
        api.get('/queue/failed'),
      ]);
      setQueueStats(statusRes.data);
      setFailedJobs(failedRes.data.data || []);
    } catch {
      // silent — no blink, no error toast
    } finally {
      if (showLoader) setQueueLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings');
      setSettings({
        ...defaultSettings,
        ...(response.data?.data || {}),
        default_sender_id: response.data?.data?.default_sender_id || '',
      });
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Start queue polling when Queue tab is active
  useEffect(() => {
    if (activeTab === 'queue') {
      fetchQueueData(true);
      queueIntervalRef.current = setInterval(() => fetchQueueData(false), 15000);
    } else {
      if (queueIntervalRef.current) {
        clearInterval(queueIntervalRef.current);
        queueIntervalRef.current = null;
      }
    }
    return () => {
      if (queueIntervalRef.current) clearInterval(queueIntervalRef.current);
    };
  }, [activeTab, fetchQueueData]);

  const handleRetryJob = async (id: number) => {
    setActionInProgress(id);
    try {
      await api.post(`/queue/failed/${id}/retry`, {});
      await fetchQueueData(false);
    } catch { /* silent */ } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteJob = async (id: number) => {
    setActionInProgress(id);
    try {
      await api.delete(`/queue/failed/${id}`);
      setFailedJobs(prev => prev.filter(j => j.id !== id));
      setQueueStats(prev => prev ? { ...prev, failed: Math.max(0, prev.failed - 1) } : prev);
    } catch { /* silent */ } finally {
      setActionInProgress(null);
    }
  };

  const handleRetryAll = async () => {
    setActionInProgress('all');
    try {
      await api.post('/queue/failed/retry-all', {});
      await fetchQueueData(false);
    } catch { /* silent */ } finally {
      setActionInProgress(null);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Delete all failed jobs? This cannot be undone.')) return;
    setActionInProgress('clear');
    try {
      await api.delete('/queue/failed');
      setFailedJobs([]);
      setQueueStats(prev => prev ? { ...prev, failed: 0 } : prev);
    } catch { /* silent */ } finally {
      setActionInProgress(null);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/settings', settings);
      alert('Settings saved successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <Icons.User size={18} /> },
    { id: 'email', label: 'Email', icon: <Icons.Mail size={18} /> },
    { id: 'security', label: 'Security', icon: <Icons.Shield size={18} /> },
    { id: 'organization', label: 'Organization', icon: <Icons.Building size={18} /> },
    { id: 'queue', label: 'Queue', icon: <Icons.Zap size={18} /> },
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '1000px' }}>
        <div style={{ height: '40px', background: '#e5e7eb', borderRadius: '8px', marginBottom: '24px', width: '200px' }} />
        <div style={{ background: 'white', borderRadius: '12px', padding: '32px', border: '1px solid #e5e7eb' }}>
          <div style={{ height: '24px', background: '#e5e7eb', borderRadius: '4px', width: '40%', marginBottom: '24px' }} />
          <div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '60%', marginBottom: '16px' }} />
          <div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '50%', marginBottom: '16px' }} />
          <div style={{ height: '48px', background: '#e5e7eb', borderRadius: '8px', width: '150px', marginTop: '32px' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Settings</h1>
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Manage your account and application preferences</p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '24px', 
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 16px',
              background: activeTab === tab.id ? '#eff6ff' : 'transparent',
              color: activeTab === tab.id ? '#2563eb' : '#6b7280',
              border: activeTab === tab.id ? '1px solid #bfdbfe' : '1px solid transparent',
              borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab.id ? 600 : 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 150ms ease-out',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Settings Form */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '32px', 
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <form onSubmit={handleSave}>
          {activeTab === 'profile' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>Profile Settings</h2>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Avatar URL
                </label>
                <input
                  type="url"
                  value={settings.avatar}
                  onChange={(e) => setSettings({ ...settings, avatar: e.target.value })}
                  placeholder="https://example.com/avatar.jpg"
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>Email Configuration</h2>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Default Sender Profile
                </label>
                <select
                  value={settings.default_sender_id}
                  onChange={(e) => setSettings({ ...settings, default_sender_id: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select sender profile</option>
                  {/* TODO: Fetch sender profiles from API */}
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Rate Limit (emails per hour)
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={settings.rate_limit_per_hour}
                  onChange={(e) => setSettings({ ...settings, rate_limit_per_hour: parseInt(e.target.value) || 100 })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Bounce Handling
                </label>
                <select
                  value={settings.bounce_handling}
                  onChange={(e) => setSettings({ ...settings, bounce_handling: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="mark">Mark as bounced</option>
                  <option value="unsubscribe">Unsubscribe contact</option>
                  <option value="delete">Delete contact</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Unsubscribe Handling
                </label>
                <select
                  value={settings.unsubscribe_handling}
                  onChange={(e) => setSettings({ ...settings, unsubscribe_handling: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="mark">Mark as unsubscribed</option>
                  <option value="delete">Delete contact</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>Security Settings</h2>
              <div style={{ 
                padding: '20px', 
                background: '#fef3c7', 
                borderRadius: '8px', 
                marginBottom: '24px',
                border: '1px solid #fcd34d'
              }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e' }}>
                  Security settings are managed at the system level. Contact your administrator for changes.
                </p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Change Password
                </label>
                <button
                  type="button"
                  onClick={() => alert('Password change feature coming soon')}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Change Password
                </button>
              </div>
            </div>
          )}

          {activeTab === 'organization' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '24px' }}>Organization Settings</h2>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Company Name
                </label>
                <input
                  type="text"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Timezone
                </label>
                <select
                  value={settings.timezone}
                  onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New York</option>
                  <option value="America/Los_Angeles">America/Los Angeles</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Date Format
                </label>
                <select
                  value={settings.date_format}
                  onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="Y-m-d">YYYY-MM-DD</option>
                  <option value="d/m/Y">DD/MM/YYYY</option>
                  <option value="m/d/Y">MM/DD/YYYY</option>
                  <option value="F j, Y">Month DD, YYYY</option>
                </select>
              </div>
            </div>
          )}

          {/* Queue Tab */}
          {activeTab === 'queue' && (
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>Queue Monitor</h2>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '24px' }}>
                Monitor background email jobs. Polls automatically every 15 seconds.
              </p>

              {queueLoading && !queueStats ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  {[1,2,3].map(i => (
                    <div key={i} style={{ height: '80px', background: '#f3f4f6', borderRadius: '10px', animation: 'pulse 1.5s infinite' }} />
                  ))}
                </div>
              ) : queueStats && (
                <>
                  {/* Stats cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    {/* Worker status */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', background: queueStats.worker_status === 'stopped' ? '#fef2f2' : queueStats.worker_status === 'idle' ? '#f9fafb' : '#f0fdf4' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: queueStats.worker_status === 'stopped' ? '#ef4444' : queueStats.worker_status === 'idle' ? '#94a3b8' : '#22c55e', display: 'inline-block', animation: queueStats.worker_status === 'active' ? 'pulse 2s infinite' : 'none' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Worker</span>
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 700, color: queueStats.worker_status === 'stopped' ? '#dc2626' : queueStats.worker_status === 'idle' ? '#64748b' : '#16a34a', textTransform: 'capitalize' }}>
                        {queueStats.worker_status}
                      </div>
                    </div>
                    {/* Pending */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', background: queueStats.pending > 0 ? '#fffbeb' : '#f9fafb' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Pending Jobs</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: queueStats.pending > 0 ? '#d97706' : '#374151' }}>{queueStats.pending}</div>
                    </div>
                    {/* Failed */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', background: queueStats.failed > 0 ? '#fef2f2' : '#f9fafb' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Failed Jobs</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: queueStats.failed > 0 ? '#dc2626' : '#374151' }}>{queueStats.failed}</div>
                    </div>
                  </div>

                  {/* Worker stopped warning */}
                  {queueStats.worker_status === 'stopped' && (
                    <div style={{ padding: '14px 16px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5', marginBottom: '24px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }}><Icons.AlertTriangle size={16} /></span>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: '0.875rem', fontWeight: 600, color: '#991b1b' }}>Queue worker is not running</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#b91c1c' }}>
                          Jobs have been waiting {queueStats.oldest_pending_minutes} min. Start the worker:
                        </p>
                        <code style={{ display: 'inline-block', marginTop: '6px', padding: '4px 8px', background: '#fee2e2', borderRadius: '4px', fontSize: '0.75rem', color: '#7f1d1d' }}>
                          php artisan queue:work --tries=3 --timeout=120
                        </code>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Failed jobs table */}
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                    Failed Jobs {failedJobs.length > 0 && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#fef2f2', color: '#dc2626', borderRadius: '9999px', fontSize: '0.75rem' }}>{failedJobs.length}</span>}
                  </h3>
                  {failedJobs.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={handleRetryAll}
                        disabled={actionInProgress !== null}
                        style={{ padding: '6px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: actionInProgress !== null ? 'not-allowed' : 'pointer', opacity: actionInProgress !== null ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '5px' }}
                      >
                        <Icons.RefreshCw size={13} />
                        {actionInProgress === 'all' ? 'Retrying…' : 'Retry All'}
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAll}
                        disabled={actionInProgress !== null}
                        style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: actionInProgress !== null ? 'not-allowed' : 'pointer', opacity: actionInProgress !== null ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '5px' }}
                      >
                        <Icons.Trash2 size={13} />
                        {actionInProgress === 'clear' ? 'Clearing…' : 'Clear All'}
                      </button>
                    </div>
                  )}
                </div>

                {failedJobs.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', background: '#f9fafb', borderRadius: '10px', border: '1px dashed #e5e7eb' }}>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.875rem' }}>No failed jobs — everything is running smoothly</p>
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Job</th>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Error</th>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Failed At</th>
                          <th style={{ padding: '10px 16px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {failedJobs.map((job, idx) => (
                          <tr key={job.id} style={{ borderTop: '1px solid #e5e7eb', background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{job.job}</span>
                            </td>
                            <td style={{ padding: '12px 16px', maxWidth: '280px' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={job.exception}>
                                {job.exception}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {new Date(job.failed_at).toLocaleString()}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <button
                                  type="button"
                                  onClick={() => handleRetryJob(job.id)}
                                  disabled={actionInProgress !== null}
                                  style={{ padding: '5px 10px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 500, cursor: actionInProgress !== null ? 'not-allowed' : 'pointer', opacity: actionInProgress === job.id ? 0.5 : 1 }}
                                >
                                  {actionInProgress === job.id ? '…' : 'Retry'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteJob(job.id)}
                                  disabled={actionInProgress !== null}
                                  style={{ padding: '5px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 500, cursor: actionInProgress !== null ? 'not-allowed' : 'pointer', opacity: actionInProgress === job.id ? 0.5 : 1 }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
            </div>
          )}

          {/* Save Button — hide on Queue tab */}
          {activeTab !== 'queue' && (
          <div style={{
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 24px',
                background: saving ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 150ms ease-out',
              }}
            >
              <Icons.Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Settings;
